/**
 * O roteamento e a porta de entrada.
 *
 * As três abas do §3 do design system existem e carregam dado real desde a `A-07` e a
 * `O-11` — o `EmBreve` que segurava o lugar das duas últimas saiu junto. Cartaz de "em
 * breve" é honesto enquanto a tela não existe e é dívida no dia seguinte.
 *
 * Enquanto a renovação da partida não responde, a aplicação não mostra nem o cockpit nem o
 * login: mostra que está conferindo. Piscar a tela de entrada para quem já está logado é o
 * tipo de detalhe que faz a aplicação parecer quebrada em toda recarga de página.
 */
import { Navigate, Route, Routes } from "react-router";

import { useSessao } from "./api/contexto-sessao";
import { Moldura } from "./componentes/Moldura";
import {
  areasDaNavegacao,
  areasVisiveis,
  eRegulador,
} from "./componentes/navegacao";
import { useModulos } from "./ganchos/useModulos";
import { useNavegacao } from "./ganchos/useNavegacao";
import { Administracao } from "./telas/Administracao";
import { Autuacao } from "./telas/Autuacao";
import { Entrada } from "./telas/Entrada";
import { FechamentoRegulatorio } from "./telas/FechamentoRegulatorio";
import { IndicesRegulatorios } from "./telas/IndicesRegulatorios";
import { Indicadores } from "./telas/Indicadores";
import { ListaDeIsentos } from "./telas/ListaDeIsentos";
import { NotificacoesInternas } from "./telas/NotificacoesInternas";
import { Operacao } from "./telas/Operacao";
import { PoderConcedente } from "./telas/PoderConcedente";
import { Prazos } from "./telas/Prazos";
import { ProcessamentoDeImagens } from "./telas/ProcessamentoDeImagens";
import { Senatran } from "./telas/Senatran";

export function App() {
  const { quem, conferindo } = useSessao();

  if (conferindo) {
    return (
      <div
        className="grid h-full place-items-center text-tinta-3"
        role="status"
      >
        Conferindo a sessão…
      </div>
    );
  }

  if (!quem) return <Entrada />;

  // O poder concedente (`OB-13`, ADR-075): só as telas de leitura dele. As outras nem
  // montam — o endereço digitado volta ao início, em vez de abrir uma tela que pediria ao
  // servidor o que ele recusa (e a recusa iria à trilha como tentativa).
  if (eRegulador(quem.perfil))
    return (
      <Routes>
        <Route element={<Moldura />}>
          <Route index element={<Inicio />} />
          <Route path="/senatran" element={<Senatran />} />
          <Route path="/prazos" element={<Prazos />} />
          <Route path="/indicadores" element={<Indicadores />} />
          <Route
            path="/indicadores/indices"
            element={<IndicesRegulatorios />}
          />
          <Route path="/poder-concedente" element={<PoderConcedente />} />
          <Route path="*" element={<Inicio />} />
        </Route>
      </Routes>
    );

  return (
    <Routes>
      <Route element={<Moldura />}>
        <Route index element={<Inicio />} />
        <Route path="/operacao" element={<Operacao />} />
        <Route
          path="/operacao/imagens"
          element={<ProcessamentoDeImagens voltarPara="/operacao" />}
        />
        <Route path="/senatran" element={<Senatran />} />
        <Route path="/senatran/isentos" element={<ListaDeIsentos />} />
        <Route
          path="/senatran/notificacoes"
          element={<NotificacoesInternas />}
        />
        <Route path="/prazos" element={<Prazos />} />
        <Route path="/imagens" element={<ProcessamentoDeImagens />} />
        <Route path="/indicadores" element={<Indicadores />} />
        <Route
          path="/indicadores/fechamento"
          element={<FechamentoRegulatorio />}
        />
        <Route path="/indicadores/indices" element={<IndicesRegulatorios />} />
        <Route path="/poder-concedente" element={<PoderConcedente />} />
        <Route path="/autuacao" element={<Autuacao />} />
        <Route path="/administracao" element={<Administracao />} />
        <Route path="*" element={<Inicio />} />
      </Route>
    </Routes>
  );
}

/**
 * A primeira tela é a primeira área que esta organização enxerga: a Operação para quem tem
 * TollVision — é por ela que o dia começa —, o Processamento de imagens para quem só
 * licenciou a Análise. Mandar todo mundo para `/operacao` levaria o cliente de upload a um
 * 402 logo depois do login.
 */
function Inicio() {
  const { quem } = useSessao();
  const { licenciados, carregando } = useModulos();
  const navegacao = useNavegacao();
  if (carregando || navegacao.carregando) return null;
  const primeira = (
    navegacao.codigos
      ? areasDaNavegacao(navegacao.codigos)
      : areasVisiveis(quem?.perfil, licenciados)
  )[0];
  return <Navigate to={primeira?.para ?? "/operacao"} replace />;
}
